import './App.css';
import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Analytics } from "@vercel/analytics/react"
import HomePage from './components/HomePage';
import Login from './components/LoginPage';
import { Dashboard } from './components/dashboard';
import Workspace from './components/Workspace';
import NewProject from './components/NewProject';
import PricingPage from './components/PricingPage';
import AboutPage from './components/AboutPage';
import SolutionPage from './components/SolutionPage';
import { RequestModal } from './components/shared';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import { OperationsProvider } from './contexts/OperationsContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { appConfig, isAlphaVersion, getAuthConfig } from './config/app.config';
import { AlphaLogin } from './components/alpha/AlphaLogin';
import { FeedbackModal } from './components/alpha/FeedbackModal';


function App() {
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  
  // Updated to check for 'user' instead of 'googleToken'
  const isLoggedIn = () => {
    const user = localStorage.getItem('user');
    return user !== null;
  };

  // Modified ProtectedRoute to exclude project routes from authentication requirement
  const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const isProjectRoute = location.pathname.match(/\/project\/[^\/]+\/?/);
    
    if (!isLoggedIn()) {
      // If this is a project route, allow access but mark as shared view
      if (isProjectRoute) {
        // Clone the child component and pass isShared prop
        return React.cloneElement(children, { isShared: true });
      }
      // For routes like dashboard, redirect to login
      return <Navigate to="/login" replace />;
    }
    
    // User is logged in, provide normal access
    return children;
  };

  const openRequestModal = () => setIsRequestModalOpen(true);
  const closeRequestModal = () => setIsRequestModalOpen(false);

  // Determine which login component to show based on environment
  const LoginComponent = isAlphaVersion() ? AlphaLogin : Login;

  return (
    <GoogleOAuthProvider clientId={getAuthConfig().googleOAuth ? process.env.REACT_APP_GOOGLE_CLIENT_ID : undefined}>
      <WebSocketProvider>
        <OperationsProvider>
          <Router>
            <div className="App">
            {/* Alpha Feedback Modal - only shows for alpha users */}
            {isAlphaVersion() && user && <FeedbackModal userInfo={user} />}
            
            <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<HomePage openRequestModal={openRequestModal} />} />
                    <Route path="/login" element={<LoginComponent />} />
                    <Route path="/about" element={<AboutPage openRequestModal={openRequestModal} />} />
                    <Route path="/solution" element={<SolutionPage openRequestModal={openRequestModal} />} />
                    <Route path="/pricing" element={<PricingPage openRequestModal={openRequestModal} />} />

                    <Route path="/workspace" element={<Workspace />} />
                    <Route path="/privacy" element={<PrivacyPage openRequestModal={openRequestModal} />} />
                    <Route path="/terms" element={<TermsPage openRequestModal={openRequestModal} />} />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/new-project"
              element={
                <ProtectedRoute>
                  <NewProject />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/*"
              element={
                <ProtectedRoute>
                  <Workspace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/file/:fileId"
              element={
                <ProtectedRoute>
                  <Workspace />
                </ProtectedRoute>
              }
            />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Analytics />
          
          {/* Global Request Modal */}
          <RequestModal 
            isOpen={isRequestModalOpen}
            onClose={closeRequestModal}
          />
          </div>
        </Router>
      </OperationsProvider>
      </WebSocketProvider>
    </GoogleOAuthProvider>
  );
}

export default App;